import React from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import Login from "../pages/auth/Login";
import TherapistDashboard from "../pages/therapist/TherapistDashboard";
import TherapistScorePage from "../pages/therapist/TherapistScorePage";
import TherapistStandings from "../pages/therapist/TherapistStandings";
import TherapistPointsPage from "../pages/therapist/TherapistPointsPage";
import WebmasterDashboard from "../pages/webmaster/WebmasterDashboard";
import TypesSettings from "../pages/webmaster/TypesSettings";
import TherapistWorkSchedule from "../pages/webmaster/TherapistWorkSchedule";
import AppointmentsAudit from "../pages/webmaster/AppointmentsAudit";
import MedicDashboard from "../pages/medic/MedicDashboard";
import MedicAppointments from "../pages/medic/MedicAppointments";
import MedicPatientsPage from "../pages/webmaster-medic/Patients";
import WebmasterMedicAppointments from "../pages/webmaster-medic/MedicAppointments";
import WebmasterMedicDashboard from "../pages/webmaster-medic/WebmasterMedicDashboard";
import ParentDashboard from "../pages/parent/ParentDashboard"; 
import Therapists from "../pages/webmaster/Therapists";
import Medics from "../pages/webmaster/Medics";
import Patient from "../pages/webmaster/Patients";
import AddTherapist from "../pages/webmaster/AddTherapist";
import AddMedic from "../pages/webmaster/AddMedic";
import EditTherapist from "../pages/webmaster/EditTherapist";
import EditMedic from "../pages/webmaster/EditMedic";
import AddPatient from "../pages/webmaster/AddPatient";
import AppointmentsTable from "../pages/webmaster/AppointmentsTable";
import PointsSettingPage from "../pages/webmaster/PointsSettingPage";
import WebmasterPatientAppointments from "../pages/webmaster/WebmasterPatientAppointments";
import TherapistScoreSummary from "../pages/webmaster/TherapistScoreSummary";
import TherapistAppointments from "../pages/therapist/TherapistAppointments";
import ProtectedRoute from "./ProtectedRoute";
import EditPatient from "../pages/webmaster/EditPatient";
import PatientAppointments from "../pages/parent/PatientAppointments";
import AddPatientMedic from "../pages/webmaster-medic/AddPatient";
import EditPatientMedic from "../pages/webmaster-medic/EditPatient";
import MedicWorkSchedule from "../pages/webmaster/MedicWorkSchedule";
import TherapistAllAppointments from "../pages/therapist/TherapistAllAppointments";
import MedicScoreTable from "../pages/webmaster/MedicScoreTable";

const AppRoutes: React.FC = () => {
  const { isAuthenticated, role } = useAuth();
  const location = useLocation();

  // Redirectionari pentru root si /webmaster când nu e autentificat
  if (location.pathname === "/webmaster" && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (location.pathname === "/" && isAuthenticated) {
    return (
      <Navigate
        to={
          role === "therapist"
            ? "/therapist/dashboard"
            : role === "medic"
            ? "/medic/dashboard"
            : role === "parent"
            ? "/parent/dashboard"
            : "/webmaster/dashboard"
        }
        replace
      />
    );
  }

  return (
    <Routes>
      {/* Redirectionare implicita in funcție de rol */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate
              to={
                role === "therapist"
                  ? "/therapist/dashboard"
                  : role === "medic"
                  ? "/medic/dashboard"
                  : role === "parent"
                  ? "/parent/dashboard"
                  : "/webmaster/dashboard"
              }
            />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Rute pentru login */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate
              to={
                role === "therapist"
                  ? "/therapist/dashboard"
                  : role === "medic"
                  ? "/medic/dashboard"
                  : role === "parent"
                  ? "/parent/dashboard"
                  : "/webmaster/dashboard"
              }
            />
          ) : (
            <Login />
          )
        }
      />

      {/* Rute pentru terapeuti */}
      <Route
        path="/therapist/dashboard"
        element={
          <ProtectedRoute role="therapist">
            <TherapistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/therapist/appointments"
        element={
          <ProtectedRoute role="therapist">
            <TherapistAppointments />
          </ProtectedRoute>
        }
      />

     <Route
        path="/therapist/all-appointments"
        element={
          <ProtectedRoute role="therapist">
            <TherapistAllAppointments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/therapist/scores"
        element={
          <ProtectedRoute role="therapist">
            <TherapistScorePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/therapist/standings"
        element={
          <ProtectedRoute role="therapist">
            <TherapistStandings />
          </ProtectedRoute>
        }
      />


      <Route
        path="/therapist/therapist-points/:therapistId"
        element={
          <ProtectedRoute role="therapist">
            <TherapistPointsPage />
          </ProtectedRoute>
        }
      />

      {/* Rute pentru medici (user role medic) */}
      <Route
        path="/medic/dashboard"
        element={
          <ProtectedRoute role="medic">
            <MedicDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/medic/appointments"
        element={
          <ProtectedRoute role="medic">
            <MedicAppointments />
          </ProtectedRoute>
        }
      />

      
      {/* Alte rute pentru medici administrate de webmaster */}
      <Route
        path="/webmaster/medics"
        element={
          <ProtectedRoute role="webmaster">
            <Medics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/medics/add-medic"
        element={
          <ProtectedRoute role="webmaster">
            <AddMedic />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/medics/edit-medic/:medicId"
        element={
          <ProtectedRoute role="webmaster">
            <EditMedic />
          </ProtectedRoute>
        }
      />

      {/* Rute pentru webmaster */}
      <Route
        path="/webmaster/dashboard"
        element={
          <ProtectedRoute role="webmaster">
            <WebmasterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/settings/types-settings"
        element={
          <ProtectedRoute role="webmaster">
            <TypesSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/settings/work-schedule"
        element={
          <ProtectedRoute role="webmaster">
            <TherapistWorkSchedule />
          </ProtectedRoute>
        }
      />
            <Route
        path="/webmaster/settings/appointments-history"
        element={
          <ProtectedRoute role="webmaster">
            <AppointmentsAudit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/therapists"
        element={
          <ProtectedRoute role="webmaster">
            <Therapists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/patients"
        element={
          <ProtectedRoute role="webmaster">
            <Patient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/therapists/add-therapist"
        element={
          <ProtectedRoute role="webmaster">
            <AddTherapist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/therapists/edit-therapist/:therapistId"
        element={
          <ProtectedRoute role="webmaster">
            <EditTherapist />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/add-patient"
        element={
          <ProtectedRoute role="webmaster">
            <AddPatient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/edit-patient/:patient_id"
        element={
          <ProtectedRoute role="webmaster">
            <EditPatient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster/therapists/appointments"
        element={
          <ProtectedRoute role="webmaster">
            <AppointmentsTable />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster/therapists/scores"
        element={
          <ProtectedRoute role="webmaster">
            <TherapistScoreSummary />
          </ProtectedRoute>
        }
      />


      <Route
        path="/webmaster/patient-appointments"
        element={
          <ProtectedRoute role="webmaster">
            <WebmasterPatientAppointments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster/therapists/points"
        element={
          <ProtectedRoute role="webmaster">
            <PointsSettingPage />
          </ProtectedRoute>
        }
      />

<Route
        path="/webmaster/standings"
        element={
          <ProtectedRoute role="webmaster">
            <TherapistScoreSummary />
          </ProtectedRoute>
        }
      />

<Route
        path="/parent/dashboard"
        element={
          <ProtectedRoute role="parent">
            <ParentDashboard />
          </ProtectedRoute>
        }
      />

<Route
        path="/parent/appointments"
        element={
          <ProtectedRoute role="parent">
            <PatientAppointments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster-medic/appointments"
        element={
          <ProtectedRoute roles={["webmaster_medic", "webmaster"]}>
            <WebmasterMedicAppointments />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster-medic/dashboard"
        element={
          <ProtectedRoute role="webmaster_medic">
            <WebmasterMedicDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster-medic/patients"
        element={
          <ProtectedRoute role="webmaster_medic">
            <MedicPatientsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster-medic/add-patient"
        element={
          <ProtectedRoute role="webmaster_medic">
            <AddPatientMedic />
          </ProtectedRoute>
        }
      />
      <Route
        path="/webmaster-medic/edit-patient/:patient_id"
        element={
          <ProtectedRoute role="webmaster_medic">
            <EditPatientMedic />
          </ProtectedRoute>
        }
      />

      <Route
        path="/webmaster/settings/medic-work-schedule"
        element={
          <ProtectedRoute role="webmaster">
            <MedicWorkSchedule />
          </ProtectedRoute>
        }
      />

       <Route
        path="/webmaster/therapists/medic-scores"
        element={
          <ProtectedRoute role="webmaster">
            <MedicScoreTable />
          </ProtectedRoute>
        }
      />

      {/* Redirecționare pentru pagini inexistente */}
      <Route
        path="*"
        element={
          isAuthenticated ? (
            <Navigate
              to={
                role === "therapist"
                  ? "/therapist/dashboard"
                   : role === "webmaster_medic"
                  ? "/webmaster-medic/dashboard"
                  : role === "medic"
                  ? "/medic/dashboard"
                  : role === "parent"
                  ? "/parent/dashboard"
                  : "/webmaster/dashboard"
              }
            />
          ) : (
            <Navigate to="/login" />
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
